import classNames from "classnames";
import React, { FC } from "react";
import { useLocalizedText } from "../../hooks/context-provider-hooks/use-localized-text";
import {
  BDS_MESSAGE_ERROR_DEFAULT_TEXT,
  BDS_MESSAGE_INFO_DEFAULT_TEXT,
  BDS_MESSAGE_SUCCESS_DEFAULT_TEXT,
  BDS_MESSAGE_WARNING_DEFAULT_TEXT,
  BDS_TEXT_TEMPLATE_KEYS,
} from "../constants";
import { IconFormSuccess, IconInfoFull, IconProhibitionFull, IconWarningTriangleFull } from "../icons";
import { CSS_CLASSES } from "./message-css-classes";

export type MessageStatusOption = "info" | "error" | "warning" | "success";

export type MessageSizeOption = "small" | "medium" | "large";

export type MessageIconOption = { [key in MessageStatusOption]: JSX.Element };

const messageIconSize: { [key in MessageSizeOption]: number } = {
  small: 18,
  medium: 22,
  large: 30,
};

const defaultIcons: MessageIconOption = {
  info: <IconInfoFull />,
  success: <IconFormSuccess />,
  warning: <IconWarningTriangleFull />,
  error: <IconProhibitionFull />,
};

export interface MessageProps {
  /**
   * A unique id to be applied to the root DOM element.
   */
  id?: string;

  /**
   * Message component supports four statuses. Every status provide another color, icon and status label.
   *
   * @default info
   */
  status?: MessageStatusOption;

  /**
   * Control if the icon and text are rendered with the status color or not.
   *
   * @default false
   */
  inactive?: boolean;

  /**
   * Controls message size.
   *
   * @default medium
   */
  size?: MessageSizeOption;

  /**
   * Control if we add the status prefix before the message text.
   * Those strings can be changed by translations in AppContext.
   *
   * @default true
   */
  showStatusLabel?: boolean;

  /**
   * Whether message text is rendered in the status color, otherwise currentColor.
   *
   * @default false
   */
  matchColor?: boolean;

  /**
   * Message component contains a set of default icons for every status, it can be overridden by this property.
   */
  icon?: JSX.Element;

  /**
   * If true - status label, message, and icon is aligned in one line, recommended for use with short messages.
   *
   * @default true
   */
  inline?: boolean;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop.
   */
  className?: string;

  /**
   * Children rendered as message. It's recommended to use a plain string or textual content (Text/Link) for this element.
   */
  children: JSX.Element | string;
}

/**
 * The `Message` component can be used to inform users about different statuses of their actions,
 * e.g. form errors/warnings, notifications about result of action.
 */
export const Message: FC<MessageProps> = ({
  id,
  status = "info",
  inactive = false,
  showStatusLabel = true,
  matchColor = false,
  inline = true,
  size = "medium",
  icon: iconProp,
  children,
  className: classNameProp,
  ...restProps
}) => {
  const localizedText = useLocalizedText(true);
  const statusLabel = React.useMemo(() => {
    if (status === "info") {
      return localizedText
        ? localizedText(BDS_TEXT_TEMPLATE_KEYS.BDS_MESSAGE_INFO, undefined, false, BDS_MESSAGE_INFO_DEFAULT_TEXT)
        : BDS_MESSAGE_INFO_DEFAULT_TEXT;
    }
    if (status === "success") {
      return localizedText
        ? localizedText(BDS_TEXT_TEMPLATE_KEYS.BDS_MESSAGE_SUCCESS, undefined, false, BDS_MESSAGE_SUCCESS_DEFAULT_TEXT)
        : BDS_MESSAGE_SUCCESS_DEFAULT_TEXT;
    }
    if (status === "error") {
      return localizedText
        ? localizedText(BDS_TEXT_TEMPLATE_KEYS.BDS_MESSAGE_ERROR, undefined, false, BDS_MESSAGE_ERROR_DEFAULT_TEXT)
        : BDS_MESSAGE_ERROR_DEFAULT_TEXT;
    }
    if (status === "warning") {
      return localizedText
        ? localizedText(BDS_TEXT_TEMPLATE_KEYS.BDS_MESSAGE_WARNING, undefined, false, BDS_MESSAGE_WARNING_DEFAULT_TEXT)
        : BDS_MESSAGE_WARNING_DEFAULT_TEXT;
    }
  }, [status, localizedText]);

  const className = classNames(
    CSS_CLASSES.MESSAGE_ROOT,
    {
      [CSS_CLASSES.MESSAGE_INLINE]: inline,
    },
    classNameProp
  );
  const iconClassName = classNames({
    [CSS_CLASSES.MESSAGE_ICON_SMALL]: size === "small",
    [CSS_CLASSES.MESSAGE_ICON_MEDIUM]: size === "medium",
    [CSS_CLASSES.MESSAGE_ICON_LARGE]: size === "large",

    [CSS_CLASSES.MESSAGE_ICON_INACTIVE]: inactive,
    [CSS_CLASSES.MESSAGE_ICON_INFO]: !inactive && status === "info",
    [CSS_CLASSES.MESSAGE_ICON_SUCCESS]: !inactive && status === "success",
    [CSS_CLASSES.MESSAGE_ICON_ERROR]: !inactive && status === "error",
    [CSS_CLASSES.MESSAGE_ICON_WARNING]: !inactive && status === "warning",
  });
  const messageContentClassName = classNames(CSS_CLASSES.MESSAGE_CONTENT, {
    [CSS_CLASSES.MESSAGE_CONTENT_INACTIVE]: inactive,
    [CSS_CLASSES.MESSAGE_CONTENT_INFO]: !inactive && matchColor && status === "info",
    [CSS_CLASSES.MESSAGE_CONTENT_SUCCESS]: !inactive && matchColor && status === "success",
    [CSS_CLASSES.MESSAGE_CONTENT_ERROR]: !inactive && matchColor && status === "error",
    [CSS_CLASSES.MESSAGE_CONTENT_WARNING]: !inactive && matchColor && status === "warning",
    [CSS_CLASSES.MESSAGE_CONTENT_SMALL]: size === "small",
    [CSS_CLASSES.MESSAGE_CONTENT_MEDIUM]: size === "medium",
    [CSS_CLASSES.MESSAGE_CONTENT_LARGE]: size === "large",
  });
  const messageLabelClassName = classNames(CSS_CLASSES.MESSAGE_LABEL, {
    [CSS_CLASSES.MESSAGE_LABEL_NO_MATCH_COLOR]: !matchColor && !inactive,
  });
  const messageTextClassName = classNames(CSS_CLASSES.MESSAGE_TEXT, {
    [CSS_CLASSES.MESSAGE_TEXT_NO_MATCH_COLOR]: !matchColor && !inactive,
  });

  const icon = React.cloneElement(iconProp || defaultIcons[status], {
    className: iconClassName,
    size: messageIconSize[size],
  });

  return (
    <div id={id} className={className} {...restProps}>
      <span>{icon}</span>
      <div className={messageContentClassName}>
        {showStatusLabel && (
          <div className={messageLabelClassName}>
            {statusLabel}
            {inline && ": "}
          </div>
        )}
        <div className={messageTextClassName}>{children}</div>
      </div>
    </div>
  );
};
